import { useNavigate } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, FileCheck, Server, Users, CheckCircle, ArrowRight, Database, Globe, ArrowLeft } from "lucide-react";
import "@/styles/govtech.css";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const PrivacyPillar = ({ icon: Icon, title, description }) => (
  <div className="text-center">
    <div className="w-20 h-20 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
      <Icon className="text-[#00703C]" size={40} />
    </div>
    <h3 className="font-bold text-[#333333] mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

const CommitmentItem = ({ title, description }) => (
  <div className="flex items-start gap-4 py-4 border-b border-gray-200 last:border-0">
    <CheckCircle className="text-[#00703C] flex-shrink-0 mt-1" size={20} />
    <div>
      <h4 className="font-semibold text-[#333333]">{title}</h4>
      <p className="text-gray-600 text-sm mt-1">{description}</p>
    </div>
  </div>
);

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Home</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#333333] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Lock className="mx-auto mb-6 text-[#00703C]" size={48} />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy by Design.<br />
            <span className="text-[#00703C]">Your Data, Your Control.</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            At TenURes, we operate on a Zero-Data Footprint model for our triage services. 
            We believe you should be able to understand your housing rights without your personal 
            information being stored in a database.
          </p>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            <PrivacyPillar
              icon={Database}
              title="Zero-Data Storage"
              description="We don't store your personal details. No names, dates of birth, or National Insurance numbers in our database."
            />
            <PrivacyPillar
              icon={EyeOff}
              title="Anonymous Triage"
              description="No login required. Complete your housing assessment without creating an account or revealing your identity."
            />
            <PrivacyPillar
              icon={FileCheck}
              title="You Own the PDF"
              description="Your Comprehensive Evidence-Based Report is generated locally on your device. We don't keep a copy."
            />
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#333333] mb-8 text-center">Our Privacy Commitment</h2>
          
          <div className="bg-white border border-gray-200 p-6">
            <CommitmentItem
              title="100% Anonymous Triage"
              description="Our eligibility tools for Succession, Right to Buy, and Mutual Exchange do not require a login. We do not collect names, dates of birth, or National Insurance numbers during the assessment."
            />
            <CommitmentItem
              title="Client-Side Processing"
              description="All calculations and logic happen within your browser session. Once you close your tab, that session data is gone. Nothing is sent to our servers during the triage process."
            />
            <CommitmentItem
              title="Audit-Ready PDF Generation"
              description="When you generate a Comprehensive Evidence-Based Report, the PDF is built locally on your device using JavaScript. We do not keep a copy, and we cannot access its contents."
            />
            <CommitmentItem
              title="Aggregated B2B Insights"
              description="To help Local Authorities manage housing stock, we may collect anonymised, high-level data (e.g., '10 residents in London checked RTB eligibility this month'). This data contains zero PII (Personally Identifiable Information) and cannot be traced back to you."
            />
            <CommitmentItem
              title="Optional Contact Only"
              description="If you choose to use features like the Exchange Matcher or SMS Reminders, you voluntarily provide contact details. These are stored securely and used only for the specific purpose you requested."
            />
            <CommitmentItem
              title="Mortgage Partner Referrals"
              description="If you express interest in mortgage advice through our Right to Buy tool, we may share your contact details with our vetted mortgage partner to help you proceed. This is clearly disclosed at the point of submission, and you can opt out at any time."
            />
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <Shield className="mx-auto mb-4 text-[#00703C]" size={40} />
            <h2 className="text-2xl font-bold text-[#333333]">Regulatory Compliance</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="text-[#00703C]" size={24} />
                <h3 className="font-bold text-[#333333]">GDPR Compliant</h3>
              </div>
              <p className="text-gray-600 text-sm">
                TenURes is built to the standards of the UK General Data Protection Regulation. 
                We practice data minimisation—only asking the questions necessary to provide your housing logic.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Server className="text-[#00703C]" size={24} />
                <h3 className="font-bold text-[#333333]">Data (Use and Access) Act 2025</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Our platform aligns with the latest UK data legislation, ensuring transparency 
                in how data is processed and giving you control over any information you choose to share.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#333333] mb-8 text-center">What We Don't Do</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Track your browsing across other websites",
              "Store your triage answers on our servers",
              "Require registration to use core features",
              "Share individual data with your landlord",
              "Use invasive cookies or trackers",
              "Sell your data without your consent"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-red-50 border border-red-100">
                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">✕</span>
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> If you choose to request mortgage advice through our Right to Buy tool, your contact details will be shared with our trusted mortgage partner. This is optional and clearly disclosed before you submit your information.
            </p>
          </div>
        </div>
      </section>

      {/* Contact for Privacy */}
      <section className="py-16 bg-[#00703C] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Questions About Your Data?</h2>
          <p className="text-green-100 mb-6">
            If you have any questions about how TenURes handles data, or if you wish to exercise 
            your rights under UK data protection law, please contact us.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-white text-[#00703C] px-6 py-3 font-semibold hover:bg-gray-100 transition-colors"
          >
            Contact Us <ArrowRight size={18} className="inline ml-2" />
          </button>
        </div>
      </section>

      {/* Legal Text */}
      <section className="py-12 bg-[#F8F8F8]">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong>Legal Notice:</strong> This Privacy Policy explains how TenURes.co.uk ("we", "our", "us") 
            handles information when you use our digital triage services. Our core triage tools operate 
            on a client-side, anonymous basis. Where you choose to provide personal information (e.g., 
            for SMS reminders or Exchange Matching), that data is processed in accordance with UK GDPR 
            and stored securely with appropriate technical measures. You have the right to access, rectify, 
            or request deletion of any personal data you have voluntarily provided. For full legal terms, 
            please contact us directly.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Last updated: {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <TenuresLogo />
          <p className="text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Privacy by Design
          </p>
        </div>
      </footer>
    </div>
  );
}
